import { useState } from 'react';
import {
  COST_ITEMS,
  DEFAULT_COST_RANGE,
  DEFAULT_STEEL_EXTRA,
  formatNumber,
  modelTexts,
} from '@indusketch/project-model';
import type { PriceCatalog, SavePriceCatalogRequest } from '@indusketch/shared';
import { adminApi } from '../../lib/api/endpoints.js';
import { userMessage } from '../../lib/errors.js';
import { useI18n } from '../../i18n/i18n.js';
import { CommitInput } from '../editor/ui/fields.js';
import { reloadPriceCatalogs, usePriceCatalogs } from '../editor/priceCatalogs.js';

const SURCHARGES = ['steelExtra', 'foundations', 'services', 'fees', 'range'] as const;
type SurchargeKey = (typeof SURCHARGES)[number];
const USUAL: Readonly<Record<SurchargeKey, number>> = {
  steelExtra: DEFAULT_STEEL_EXTRA,
  foundations: 0,
  services: 0,
  fees: 0,
  range: DEFAULT_COST_RANGE,
};

/** Text of an amount for editing: empty when the guide value applies. */
const editable = (v: number | undefined) =>
  v === undefined ? '' : String(Math.round(v * 100) / 100).replace('.', ',');
/** Empty means "guide value"; anything else must be a number of at least 0. */
function parseAmount(text: string): number | undefined | null {
  const trimmed = text.replace(/[\s€%]/g, '').replace(',', '.');
  if (trimmed === '') return undefined;
  const v = Number(trimmed);
  return Number.isFinite(v) && v >= 0 ? v : null;
}

type Amounts = Readonly<Record<string, number | undefined>>;
const sameAmounts = (a: Amounts = {}, b: Amounts = {}) => {
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((k) => a[k] === b[k]);
};

const draftOf = (c: PriceCatalog): SavePriceCatalogRequest => ({
  name: c.name,
  country: c.country,
  isDefault: c.isDefault,
  prices: { ...c.prices },
  surcharges: { ...c.surcharges },
});

/** Admins' price catalogs: projects start from the default or the one they choose. */
export function PriceCatalogsTab() {
  const { m, fmt } = useI18n();
  const t = m.admin;
  const catalogs = usePriceCatalogs();
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const current = catalogs?.find((x) => x.id === selected) ?? catalogs?.[0];

  const run = async (action: () => Promise<string | null>) => {
    try {
      setError(null);
      setSaved(false);
      const next = await action();
      await reloadPriceCatalogs();
      if (next) setSelected(next);
      setSaved(true);
    } catch (err) {
      setError(userMessage(err));
    }
  };
  if (!catalogs || !current) return <section className="card block-card" />;

  return (
    <section className="card block-card" data-testid="admin-prices">
      <p className="muted prices-intro">{t.pricesIntro}</p>
      <div className="seg price-catalogs" role="group" aria-label={t.pricesCatalogs}>
        {catalogs.map((x) => (
          <button
            key={x.id}
            type="button"
            className={`btn${x.id === current.id ? ' btn-active' : ''}`}
            onClick={() => {
              setSelected(x.id);
              setSaved(false);
            }}
            data-testid={`price-catalog-${x.name}`}
          >
            {x.name}
            {x.isDefault && <span className="count-badge">{t.pricesDefaultBadge}</span>}
          </button>
        ))}
        <button
          type="button"
          className="btn"
          onClick={() =>
            void run(async () => {
              const r = await adminApi.createPriceCatalog({
                ...draftOf(current),
                name: fmt(t.pricesCopyName, { name: current.name }).slice(0, 80),
                isDefault: false,
              });
              return r.catalog.id;
            })
          }
          data-testid="price-catalog-copy"
        >
          {t.pricesCopy}
        </button>
      </div>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <CatalogEditor
        key={`${current.id}-${current.updatedAt}`}
        catalog={current}
        saved={saved}
        onSave={(draft) =>
          run(async () => {
            await adminApi.updatePriceCatalog(current.id, draft);
            return null;
          })
        }
        onDelete={() => {
          if (!window.confirm(fmt(t.pricesConfirmDelete, { name: current.name }))) return;
          void run(async () => {
            await adminApi.deletePriceCatalog(current.id);
            return catalogs.find((x) => x.id !== current.id)?.id ?? null;
          });
        }}
      />
    </section>
  );
}

/** Name, country, default and every unit price and surcharge of one catalog. */
function CatalogEditor({
  catalog,
  saved,
  onSave,
  onDelete,
}: {
  catalog: PriceCatalog;
  saved: boolean;
  onSave: (draft: SavePriceCatalogRequest) => Promise<void>;
  onDelete: () => void;
}) {
  const { m } = useI18n();
  const t = m.admin;
  const c = modelTexts().costs;
  const [draft, setDraft] = useState(() => draftOf(catalog));
  const setPrice = (id: string, v: number | undefined) => {
    const { [id]: _old, ...rest } = draft.prices;
    setDraft({ ...draft, prices: v === undefined ? rest : { ...rest, [id]: v } });
  };
  const setSurcharge = (key: SurchargeKey, v: number | undefined) => {
    const { [key]: _old, ...rest } = draft.surcharges;
    setDraft({ ...draft, surcharges: v === undefined ? rest : { ...rest, [key]: v } });
  };
  const surchargeLabel = (key: SurchargeKey) =>
    key === 'steelExtra'
      ? m.editor.costs.steelExtra
      : key === 'range'
        ? m.editor.costs.range
        : c.surcharges[key];
  const amount = (
    value: number | undefined,
    guide: string,
    unit: string,
    label: string,
    onChange: (v: number | undefined) => void,
    testId: string,
  ) => (
    <span className={`price-input${value === undefined ? '' : ' is-set'}`}>
      <CommitInput
        value={editable(value)}
        inputMode="decimal"
        aria-label={label}
        placeholder={guide}
        onCommit={(text) => {
          const v = parseAmount(text);
          if (v === null) return false;
          onChange(v);
          return true;
        }}
        data-testid={testId}
      />
      <span className="price-unit">{unit}</span>
    </span>
  );
  const stored = draftOf(catalog);
  const dirty =
    draft.name !== stored.name ||
    draft.country !== stored.country ||
    draft.isDefault !== stored.isDefault ||
    !sameAmounts(draft.prices, stored.prices) ||
    !sameAmounts(draft.surcharges, stored.surcharges);

  return (
    <div className="price-layout">
      <aside className="price-meta">
        <h3 className="block-title">{t.pricesCatalog}</h3>
        <label className="field">
          <span>{t.pricesName}</span>
          <input
            className="input"
            value={draft.name}
            maxLength={80}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            data-testid="price-catalog-name"
          />
        </label>
        <label className="field">
          <span>{t.pricesCountry}</span>
          <select
            className="input"
            value={draft.country}
            onChange={(e) => setDraft({ ...draft, country: e.target.value === 'pl' ? 'pl' : 'de' })}
          >
            <option value="de">{m.editor.project.countries.de}</option>
            <option value="pl">{m.editor.project.countries.pl}</option>
          </select>
        </label>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={draft.isDefault === true}
            disabled={catalog.isDefault}
            onChange={(e) => setDraft({ ...draft, isDefault: e.target.checked })}
            data-testid="price-catalog-default"
          />
          {t.pricesIsDefault}
        </label>
        <div className="price-actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={draft.name.trim() === ''}
            onClick={() => void onSave(draft)}
            data-testid="price-catalog-save"
          >
            {t.pricesSave}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={catalog.isDefault}
            onClick={onDelete}
          >
            {t.pricesDelete}
          </button>
          {dirty ? (
            <span className="price-status is-dirty">{t.pricesUnsaved}</span>
          ) : (
            saved && (
              <span className="price-status" data-testid="price-catalog-saved">
                {t.pricesSaved}
              </span>
            )
          )}
        </div>
      </aside>
      <div className="table-wrap">
        <table className="table price-table">
          <thead>
            <tr>
              <th>{t.pricesItem}</th>
              <th className="num col-guide">{t.pricesGuide}</th>
              <th className="num">{t.pricesCatalogPrice}</th>
            </tr>
          </thead>
          <tbody>
            <tr className="table-group">
              <th colSpan={3}>{t.pricesUnitPrices}</th>
            </tr>
            {COST_ITEMS.map((item) => (
              <tr key={item.id}>
                <td>{c.items[item.id]}</td>
                <td className="num muted col-guide">
                  {formatNumber(item.price, 0)} {c.perUnit[item.unit]}
                </td>
                <td className="num">
                  {amount(
                    draft.prices[item.id],
                    formatNumber(item.price, 0),
                    c.perUnit[item.unit],
                    `${c.items[item.id]} (${c.perUnit[item.unit]})`,
                    (v) => setPrice(item.id, v),
                    `price-catalog-price-${item.id}`,
                  )}
                </td>
              </tr>
            ))}
            <tr className="table-group">
              <th colSpan={3}>{t.pricesSurcharges}</th>
            </tr>
            {SURCHARGES.map((key) => (
              <tr key={key}>
                <td>{surchargeLabel(key)}</td>
                <td className="num muted col-guide">{formatNumber(USUAL[key], 1)} %</td>
                <td className="num">
                  {amount(
                    draft.surcharges[key],
                    formatNumber(USUAL[key], 1),
                    '%',
                    `${surchargeLabel(key)} (%)`,
                    (v) => setSurcharge(key, v),
                    `price-catalog-surcharge-${key}`,
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
